// components/UI/HeaderAvailabilityWidget.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getMyDetailsThunk,
  getMyAvailabilityThunk,
  updateMyStatusThunk,
  toggleAcceptingAppointmentsThunk,
} from "../../store/thunks/adminThunks";
import {
  StatusOnlineIcon,
  ClockIcon,
  CalendarIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  UserIcon,
  BellIcon,
  XCircleIcon,
} from "@heroicons/react/outline";

const HeaderAvailabilityWidget = () => {
  const dispatch = useDispatch();
  const { myCurrentStatus, myAvailability, appointmentStats, currentAdmin } =
    useSelector((state) => state.admin);
  const { user, isRootAdmin } = useSelector((state) => state.auth);

  const [showDropdown, setShowDropdown] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTogglingAccepting, setIsTogglingAccepting] = useState(false);
  const dropdownRef = useRef(null);

  console.log("My Current Status:", myCurrentStatus);

  // Enhanced status options using theme colors
  // Enhanced status options
  const statusOptions = [
    {
      value: "AVAILABLE",
      label: "Available",
      dropdownIcon: "✅",
      displayIcon: (
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-green-100">
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
        </div>
      ),
      color: "bg-green-500",
      textColor: "text-green-700",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
    },
    {
      value: "BUSY",
      label: "Busy",
      dropdownIcon: "⚡",
      displayIcon: (
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-yellow-100">
          <ClockIcon className="w-5 h-5 text-yellow-600" />
        </div>
      ),
      color: "bg-yellow-500",
      textColor: "text-yellow-700",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
    },
    {
      value: "IN_APPOINTMENT",
      label: "In Meeting",
      dropdownIcon: "👥",
      displayIcon: (
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100">
          <UserIcon className="w-5 h-5 text-blue-600" />
        </div>
      ),
      color: "bg-blue-500",
      textColor: "text-blue-700",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
    },
    {
      value: "ON_BREAK",
      label: "On Break",
      dropdownIcon: "☕",
      displayIcon: (
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-orange-100">
          <svg
            className="w-5 h-5 text-orange-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
          </svg>
        </div>
      ),
      color: "bg-orange-500",
      textColor: "text-orange-700",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
    },
    {
      value: "OFF_DUTY",
      label: "Off Duty",
      dropdownIcon: "🏠",
      displayIcon: (
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100">
          <XCircleIcon className="w-5 h-5 text-gray-600" />
        </div>
      ),
      color: "bg-gray-500",
      textColor: "text-gray-700",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
    },
    {
      value: "AWAY",
      label: "Away",
      dropdownIcon: "🚫",
      displayIcon: (
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-red-100">
          <svg
            className="w-5 h-5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>
      ),
      color: "bg-red-500",
      textColor: "text-red-700",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
    },
  ];

  // Load data on mount
  useEffect(() => {
    if (user && (isRootAdmin || currentAdmin?.role === "admin")) {
      dispatch(getMyAvailabilityThunk());
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  // Don't show if not admin
  if (!currentAdmin || (!isRootAdmin && currentAdmin.role !== "admin")) {
    return null;
  }

  const currentStatus =
    statusOptions.find((s) => s.value === myCurrentStatus?.status) ||
    statusOptions[4];

  const upcomingCount = appointmentStats?.upcomingAppointmentsCount || 0;
  const totalCompleted = appointmentStats?.completedAppointments || 0;
  const rating = appointmentStats?.averageRating || 0;

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    setIsUpdating(true);
    try {
      await dispatch(
        updateMyStatusThunk({
          status: newStatus,
        })
      ).unwrap();
      setShowDropdown(false);
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle toggle accepting appointments
  const handleToggleAccepting = async (e) => {
    e.stopPropagation();
    setIsTogglingAccepting(true);
    try {
      await dispatch(
        toggleAcceptingAppointmentsThunk(!myAvailability?.acceptsAppointments)
      ).unwrap();

      // Refresh availability data to get the updated state
      await dispatch(getMyAvailabilityThunk()).unwrap();
    } catch (error) {
      console.error("Failed to toggle accepting appointments:", error);
    } finally {
      setIsTogglingAccepting(false);
    }
  };

  return (
    <div className="relative h-full flex items-center" ref={dropdownRef}>
      {/* Full Height Widget Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="h-full flex items-center gap-2 px-3 hover:bg-white hover:bg-opacity-10 transition-all duration-200"
      >
        {/* Descriptive Status Icon */}
        <div className="relative">
          {currentStatus.displayIcon}

          {/* Accepting Appointments Indicator */}
          {myAvailability?.acceptsAppointments && (
            <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success border-2 border-white animate-pulse" />
          )}
        </div>

        {/* Status Label - Desktop Only */}
        <div className="hidden lg:flex items-center gap-2">
          <span className="text-sm font-semibold text-text-on-primary">
            {currentStatus.label}
          </span>
          <ChevronDownIcon
            className={`w-4 h-4 text-text-on-primary transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Compact Stats - Extra Large Screens */}
        {upcomingCount > 0 && (
          <div className="hidden xl:flex items-center gap-1 px-2 py-1 bg-white bg-opacity-20 rounded-full">
            <CalendarIcon className="w-3 h-3 text-text-on-primary" />
            <span className="text-xs font-bold text-text-on-primary">
              {upcomingCount}
            </span>
          </div>
        )}

        {/* Mobile Chevron */}
        <ChevronDownIcon
          className={`w-5 h-5 text-text-on-primary lg:hidden transition-transform duration-200 ${
            showDropdown ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-background rounded-xl shadow-2xl border border-primary/10 z-50 overflow-hidden">
          {/* Header with Emerald-Gold Gradient */}
          <div className="px-4 py-3 bg-gradient-to-r from-primary to-primary-light flex items-center justify-between">
            <div className="text-text-on-primary">
              <p className="font-bold text-sm">
                {currentAdmin?.name?.split(" ")[0] || "Admin"}
              </p>
              <p className="text-xs opacity-90 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary"></span>
                {currentStatus.label}
              </p>
            </div>
            <StatusOnlineIcon className="w-6 h-6 text-text-on-primary opacity-80" />
          </div>

          {/* Quick Stats */}
          <div className="px-3 py-3 bg-neutral-light grid grid-cols-3 gap-2 border-b border-primary/10">
            <div className="text-center">
              <CalendarIcon className="w-4 h-4 text-primary mx-auto mb-0.5" />
              <p className="text-lg font-bold text-text">{upcomingCount}</p>
              <p className="text-xs text-neutral-dark">Upcoming</p>
            </div>
            <div className="text-center border-x border-primary/10">
              <CheckCircleIcon className="w-4 h-4 text-success mx-auto mb-0.5" />
              <p className="text-lg font-bold text-text">{totalCompleted}</p>
              <p className="text-xs text-neutral-dark">Done</p>
            </div>
            <div className="text-center">
              <span className="text-lg mb-0.5 block">⭐</span>
              <p className="text-lg font-bold text-text">
                {rating > 0 ? rating.toFixed(1) : "-"}
              </p>
              <p className="text-xs text-neutral-dark">Rating</p>
            </div>
          </div>

          {/* Status Options */}
          <div className="p-2">
            <p className="px-2 py-1 text-xs font-semibold text-neutral-dark uppercase tracking-wider">
              Change Status
            </p>
            <div className="space-y-1 max-h-60 overflow-y-auto custom-scrollbar">
              {statusOptions.map((status) => {
                const isActive = currentStatus.value === status.value;
                return (
                  <button
                    key={status.value}
                    onClick={() => handleStatusChange(status.value)}
                    disabled={isUpdating}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all ${
                      isActive
                        ? `${status.bgColor} ${status.borderColor} border ${status.textColor} font-medium`
                        : "hover:bg-neutral-light text-text"
                    } ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span className="text-xl">{status.dropdownIcon}</span>
                    <span className="flex-1 text-left text-sm">
                      {status.label}
                    </span>
                    {isActive && (
                      <div
                        className={`w-2 h-2 rounded-full ${status.color}`}
                      ></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Accepting Toggle */}
          <div className="px-4 py-2.5 bg-neutral-light border-t border-primary/10">
            <button
              onClick={handleToggleAccepting}
              disabled={isTogglingAccepting}
              className="w-full flex items-center justify-between hover:bg-background rounded-lg p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2">
                <BellIcon className="w-4 h-4 text-neutral-dark" />
                <span className="text-sm text-text">Accepting</span>
              </div>
              <div className="relative">
                {isTogglingAccepting ? (
                  <div className="w-10 h-6 rounded-full bg-neutral-light flex items-center justify-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent" />
                  </div>
                ) : (
                  <div
                    className={`relative w-10 h-6 rounded-full transition-colors duration-200 ${
                      myAvailability?.acceptsAppointments
                        ? "bg-success"
                        : "bg-neutral-dark/30"
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-200 ${
                        myAvailability?.acceptsAppointments
                          ? "translate-x-4"
                          : "translate-x-0"
                      }`}
                    />
                  </div>
                )}
              </div>
            </button>
          </div>

          {/* Footer with Emerald-Gold Gradient */}
          <div className="p-2 border-t border-primary/10">
            <a
              href="/my-availability"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-text-on-primary text-sm font-medium rounded-lg transition-all shadow-sm hover:shadow-md"
              onClick={() => setShowDropdown(false)}
            >
              <ClockIcon className="w-4 h-4" />
              Manage Availability
            </a>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isUpdating && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center rounded-lg">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: var(--color-neutral-light);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--color-primary);
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: var(--color-primary-dark);
        }
      `}</style>
    </div>
  );
};

export default HeaderAvailabilityWidget;
